// Turning push notifications on and off for this device, and the glue between the service worker and the app.
// The browser's permission prompt only ever appears from enablePush(), which the UI calls from a click: browsers
// (Safari especially) refuse prompts that aren't a direct response to the person, and an unprompted ask is rude.
import { api } from "./api.ts";

export type PushStatus =
  /** No service worker / Push API (an old browser, or a private window). */
  | "unsupported"
  /** iPhone/iPad in Safari: push only works once Bloom is added to the Home Screen (iOS 16.4+). */
  | "ios-needs-install"
  /** The server has no VAPID key (push isn't set up on this deployment). */
  | "unconfigured"
  /** The person blocked notifications for Bloom in the browser. */
  | "denied"
  | "off"
  | "on";

export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () =>
  matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
/** A phone or tablet's browser, not a computer's (#683): the server holds a push for these while a desktop is in use. */
export const isMobileForm = (): boolean => {
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  return uaData?.mobile ?? (isIOS() || /Android|Mobile/i.test(navigator.userAgent));
};
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

let serverKey: Promise<string | null> | null = null;
// A failed fetch isn't remembered: offline at start mustn't read as "push isn't set up" until the next reload.
const getKey = () =>
  (serverKey ??= api<{ key: string | null }>("/push/key").then(
    (r) => r.key,
    () => {
      serverKey = null;
      return null;
    },
  ));

async function registration(): Promise<ServiceWorkerRegistration> {
  // main.tsx registers the worker in production builds; make sure it exists in any case before subscribing.
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) await navigator.serviceWorker.register("/sw.js");
  return navigator.serviceWorker.ready;
}

function keyBytes(b64u: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64u.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (b64u.length % 4)) % 4));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

const sameKey = (a: ArrayBuffer | null, b: Uint8Array) => !!a && a.byteLength === b.length && new Uint8Array(a).every((x, i) => x === b[i]);

export async function pushStatus(): Promise<PushStatus> {
  // Every iPhone browser is Safari underneath, and none of them can push from a tab.
  if (isIOS() && !isStandalone()) return "ios-needs-install";
  if (!supported()) return "unsupported";
  if (!(await getKey())) return "unconfigured";
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission !== "granted") return "off";
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ? "on" : "off";
}

/**
 * pushStatus, made honest about the server: when this browser has a subscription, saves it again under this sign-in
 * session. The browser's subscription can outlive the server's row (migration 0018 dropped every row; a session that
 * ends or deliveries that keep failing remove one), and then "on" would be wrong: nothing arrives. The save is an
 * upsert, so this is safe whenever the setting is shown. If the server refuses it, this device is off. Never prompts.
 */
export async function checkPush(): Promise<PushStatus> {
  const status = await pushStatus();
  const key = await getKey();
  if (status !== "on" || !key) return status;
  try {
    await subscribe(key);
    return "on";
  } catch {
    return "off";
  }
}

/** Asks for permission (call from a click), subscribes this browser and saves it on the server. */
export async function enablePush(): Promise<PushStatus> {
  if (!supported()) return pushStatus();
  const key = await getKey();
  if (!key) return "unconfigured";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "off";
  await subscribe(key);
  return "on";
}

async function subscribe(key: string) {
  const reg = await registration();
  const want = keyBytes(key);
  let sub = await reg.pushManager.getSubscription();
  // The server's key changed (rotated): the old subscription can't receive our pushes any more.
  if (sub && !sameKey(sub.options.applicationServerKey, want)) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: want });
  await api("/push/subscribe", { body: { ...sub.toJSON(), form: isMobileForm() ? "mobile" : "desktop" } });
}

/**
 * Takes this browser's subscription off the account and off the push service. A push subscription follows the sign-in
 * session that made it (#230), so sign-out calls this before it ends the session: the server only accepts the
 * unsubscribe while the sign-in cookie still works. No-op when this browser has no subscription.
 */
export async function dropPushSubscription(): Promise<void> {
  if (!supported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api("/push/unsubscribe", { body: { endpoint: sub.endpoint } }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
}

export async function disablePush(): Promise<PushStatus> {
  await dropPushSubscription();
  return pushStatus();
}

/**
 * If this browser already has a subscription, saves it under the current sign-in session. The server ties a
 * subscription to the session that saved it (#230), so call this after a new session starts: a sign-in, or a password
 * change that signs out other devices (it replaces this session too). Never prompts.
 */
export async function registerPush(): Promise<void> {
  if (!supported() || Notification.permission !== "granted") return;
  const key = await getKey();
  const reg = await navigator.serviceWorker.getRegistration();
  if (key && (await reg?.pushManager.getSubscription())) await subscribe(key).catch(() => {});
}

/** Sends a test notification to every device I've turned on (checks real delivery end to end). */
export const testPush = () => api<{ devices: number; sent: number; failed: number; removed: number }>("/push/test", { body: {} });

/**
 * On start: listen for the service worker asking us to open a channel after a notification tap. (Saving this
 * device's current subscription, in case it was dropped or the browser rotated it, is registerPush; the session
 * provider calls it once someone is signed in.) Never prompts.
 */
export function installPush() {
  if (!supported()) return;
  navigator.serviceWorker.addEventListener("message", (e: MessageEvent<{ t?: string; url?: string }>) => {
    // The service worker asks which page we're on before showing a notification (see sw.js).
    if (e.data?.t === "bloom:where") return e.ports[0]?.postMessage({ path: location.pathname });
    if (e.data?.t !== "bloom:open" || !e.data.url?.startsWith("/")) return;
    if (location.pathname === e.data.url) return;
    // Client-side navigation: react-router follows popstate, so the open app switches channel without a reload.
    history.pushState(null, "", e.data.url);
    dispatchEvent(new PopStateEvent("popstate"));
  });
  // Messages from the service worker wait in a queue until this is called (addEventListener doesn't start it).
  navigator.serviceWorker.startMessages();
}

/** Clears this channel's notifications from the device once it's on screen. */
export function clearNotifications(tag: string) {
  if (!supported()) return;
  void navigator.serviceWorker
    .getRegistration()
    .then((reg) => reg?.getNotifications({ tag }))
    .then((list) => list?.forEach((n) => n.close()))
    .catch(() => {});
}
